import { useTranslation } from "@sagot/i18n";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Icon } from "@sagot/ui";
import type { AssignableMember } from "./gap-row-menu";

interface GapBulkBarProps {
  count: number;
  members: readonly AssignableMember[] | null;
  pending: boolean;
  onAssign: (member: AssignableMember) => void;
  onIgnore: () => void;
  onClear: () => void;
}

/** Selection actions for the visible tab (assign / ignore / clear). */
export function GapBulkBar({ count, members, pending, onAssign, onIgnore, onClear }: GapBulkBarProps) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-primary-soft px-5 py-2.5" role="region" aria-label={t("engage.gaps.bulk.selected", { count })}>
      <span className="text-sm font-bold" aria-live="polite">
        {t("engage.gaps.bulk.selected", { count })}
      </span>
      {members && members.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" disabled={pending}>
              <Icon name="user-plus" />
              {t("engage.gaps.bulk.assignTo")}
              <Icon name="chevron-down" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            {members.map((member) => (
              <DropdownMenuItem
                key={member.userId}
                onSelect={() => {
                  onAssign(member);
                }}
              >
                {member.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <Button variant="secondary" size="sm" disabled={pending} onClick={onIgnore}>
        <Icon name="eye-off" />
        {t("engage.gaps.bulk.ignore")}
      </Button>
      <Button variant="ghost" size="sm" className="ml-auto" onClick={onClear}>
        {t("engage.gaps.bulk.clear")}
      </Button>
    </div>
  );
}
